import {
  AllowedFormats,
  intlDate,
} from "@faire/web--source/common/localization/date-fns/intlDate";
import { isSameMonth } from "date-fns/isSameMonth";

/*
Date ranges cannot be placeholders since the way date ranges format vary by language and use case.
Therefore, we have a restriction that only single date values can be placeholders, never ranges.
This result is quite verbose, but it allows translators to make decisions based on the situation.

Please see localDateRange.test.ts for examples of proper usage.

More information can be found here: faire.link/l10n
 */

export interface ISameMonthArgs {
  // The name of the month of the starting date
  startMonthDate: string;
  // The number day of the month of the starting date
  startDayNumber: string;
  // The number day of the month of the ending date
  endDayNumber: string;
}
type GetSameMonthString = (
  dateRangePlaceholderValues: ISameMonthArgs
) => string;

export interface IDifferentMonthArgs {
  // The formatted start date
  startDate: string;
  // The formatted end date
  endDate: string;
}
type GetDifferentMonthString = (
  dateRangePlaceholderValues: IDifferentMonthArgs
) => string;

export type AllowedMonthFormats = "MMM" | "MMMM";

// Both same month and different month formatters are always required
export type DateRangeOptions = {
  // A formatter for when the ranges fall in the same month
  sameMonthFormatter: GetSameMonthString;
  // A formatter for when the ranges fall in different months
  differentMonthFormatter: GetDifferentMonthString;
  // How the month should be formatted
  monthFormat?: AllowedMonthFormats;
};

const dateFormatForAllowedMonthFormats: Record<
  AllowedMonthFormats,
  AllowedFormats
> = {
  ["MMM"]: "MMM d",
  ["MMMM"]: "MMMM d",
};

export const localDateRange = (
  startDate: number,
  endDate: number,
  formatters: DateRangeOptions
): string => {
  const monthFormat = formatters.monthFormat ?? "MMMM";
  const shouldDisplaySameMonth = isSameMonth(startDate, endDate);
  if (shouldDisplaySameMonth) {
    const startMonthDate = intlDate(startDate, monthFormat);
    const startDayNumber = intlDate(startDate, "d");
    const endDayNumber = intlDate(endDate, "d");
    return formatters.sameMonthFormatter!({
      startMonthDate,
      startDayNumber,
      endDayNumber,
    });
  }

  const dateFormat = dateFormatForAllowedMonthFormats[monthFormat];
  const formattedStartDate = intlDate(startDate, dateFormat);
  const formattedEndDate = intlDate(endDate, dateFormat);
  return formatters.differentMonthFormatter!({
    startDate: formattedStartDate,
    endDate: formattedEndDate,
  });
};
